import { useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Upload } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { uploadArtwork } from "@/components/signs/useSignConfig";
import { Field, Primary, Sheet, copyText, inputCls, money } from "@/components/backoffice/kit";

/** 8.3 Ready: the locker code (pickup) or tracking number + carrier (ship), then Save. */
export function ReadySheet({ order, pickupMode, carriers, onDone, onClose, base = "/vendor/orders" }) {
    const ship = order.handoff === "ship";
    const [code, setCode] = useState(order.locker_code || "");
    const [num, setNum] = useState(order.tracking_number || "");
    const [carrier, setCarrier] = useState(order.tracking_carrier || (carriers?.[0] || "USPS"));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const save = async () => {
        setBusy(true); setErr("");
        try {
            const { data } = await api.post(`${base}/${order.id}/advance`, {
                status: "ready", locker_code: ship ? null : code, tracking_number: ship ? num : null, carrier: ship ? carrier : null });
            onDone(data.order);
        } catch (e) { setErr(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    return (
        <Sheet title={ship ? "Mark shipped" : "Mark ready"} onClose={onClose} testid="ready-sheet">
            {ship ? (
                <div className="grid gap-3">
                    <Field label="Tracking number" testid="ready-tracking-field"><input className={inputCls} value={num} onChange={(e) => setNum(e.target.value)} data-testid="ready-tracking" autoFocus /></Field>
                    <Field label="Carrier">
                        <select className={inputCls} value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="ready-carrier">
                            {(carriers || ["USPS", "UPS", "FedEx", "DHL", "Other"]).map((c) => <option key={c}>{c}</option>)}
                        </select>
                    </Field>
                </div>
            ) : (
                <Field label={pickupMode === "locker" ? "Locker code" : "Pickup note (optional)"}
                    hint={pickupMode === "locker" ? "Only this customer sees it, in their tracker and their message." : "Counter pickup — a code is optional."}>
                    <input className={inputCls} value={code} onChange={(e) => setCode(e.target.value)} data-testid="ready-locker" autoFocus />
                </Field>
            )}
            {err && <p className="mt-2 text-[15px] text-[#b3261e]" data-testid="ready-error">{err}</p>}
            <div className="mt-3"><Primary onClick={save} disabled={busy} testid="ready-save" full>Save</Primary></div>
        </Sheet>
    );
}

/** 8.3 / 8.4 Send proof: pick an image already on the order or upload one, then Send. */
export function ProofSheet({ order, onDone, onClose, base = "/vendor/orders" }) {
    const images = [];
    (order.items || []).concat(order.consigned_items || []).forEach((i) => {
        (i.artwork || []).forEach((a) => a.url && images.push({ uid: i.uid, url: a.url, label: a.filename || "artwork" }));
        if (i.proof?.url) images.push({ uid: i.uid, url: i.proof.url, label: "current proof" });
    });
    const [pick, setPick] = useState(images[0] || null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const file = useRef(null);
    const upload = async (f) => {
        if (!f) return;
        setBusy(true);
        try { const rec = await uploadArtwork(f); setPick({ uid: (order.items?.[0] || {}).uid, url: rec.url, label: rec.filename, uploaded: true }); }
        catch { setErr("Upload failed. Try again."); } finally { setBusy(false); }
    };
    const send = async () => {
        if (!pick) return setErr("Pick or upload an image first");
        setBusy(true); setErr("");
        try {
            const { data } = await api.post(`${base}/${order.id}/proof`, { uid: pick.uid, url: pick.url, filename: pick.label });
            toast.success(data.email?.sent ? "Proof sent" : `Proof saved — email ${data.email?.reason || "not sent"}`);
            onDone(data.order);
        } catch (e) { setErr(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    return (
        <Sheet title="Send proof" onClose={onClose} testid="proof-sheet">
            <div className="grid grid-cols-3 gap-2" data-testid="proof-images">
                {images.map((im, n) => (
                    <button key={`${im.url}-${n}`} onClick={() => setPick(im)} data-testid={`proof-pick-${n}`}
                        className={`border-2 bg-white p-1 ${pick?.url === im.url ? "border-[var(--accent)]" : "border-[#767676]"}`}>
                        <img src={im.url} alt="" className="h-20 w-full object-contain" />
                        <span className="block truncate text-[12px]">{im.label}</span>
                    </button>
                ))}
                <button onClick={() => file.current?.click()} data-testid="proof-upload" className="flex min-h-[96px] flex-col items-center justify-center border-2 border-dashed border-[#767676] bg-white text-[14px]">
                    <Upload size={18} /> Upload
                </button>
                <input ref={file} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            </div>
            {pick?.uploaded && <p className="mt-2 text-[14px] text-[#1e6b34]" data-testid="proof-uploaded">Uploaded: {pick.label}</p>}
            {err && <p className="mt-2 text-[15px] text-[#b3261e]" data-testid="proof-error">{err}</p>}
            <div className="mt-3"><Primary onClick={send} disabled={busy || !pick} testid="proof-send" full>{order.proof_badge === "waiting" ? "Resend" : "Send"}</Primary></div>
        </Sheet>
    );
}

const REASONS = ["Balance", "Rush", "Artwork", "Job change"];

/** 8.3 Send pay link: reason + amount → builds /pay?amount=&for=&order= and copies it. Nothing is sent. */
export function PayLinkSheet({ order, rushPct, artworkDefault = 35, onDone, onClose, base = "/vendor/orders" }) {
    const [reason, setReason] = useState("Balance");
    const [rush, setRush] = useState("1 week");
    const [amount, setAmount] = useState(order.balance_due > 0 ? String(order.balance_due) : "");
    const [link, setLink] = useState("");
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const pickReason = (r) => {
        setReason(r);
        if (r === "Rush") setAmount(String(Math.round(Number(order.shop_total || order.gross_total || order.my_total || 0) * ((rushPct?.[rush] || 75) / 100) * 100) / 100));
        if (r === "Artwork") setAmount(String(artworkDefault));
        if (r === "Balance") setAmount(order.balance_due > 0 ? String(order.balance_due) : "");
    };
    const pickRush = (k) => { setRush(k); setAmount(String(Math.round(Number(order.shop_total || order.gross_total || order.my_total || 0) * ((rushPct?.[k] || 75) / 100) * 100) / 100)); };
    const build = async () => {
        const n = Number(amount);
        if (!(n > 0)) return toast.error("Enter an amount");
        setBusy(true);
        try {
            const purpose = reason === "Rush" ? `Rush (${rush})` : reason === "Artwork" ? "Artwork (vector redraw)" : reason === "Job change" ? "Job change" : "Balance";
            const { data } = await api.post(`${base}/${order.id}/balance`, { amount: n, purpose, notify: false });
            const url = `${window.location.origin}${data.pay_link}`;
            setLink(url);
            setCopied(await copyText(url));
            onDone?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    return (
        <Sheet title="Send pay link" onClose={onClose} testid="paylink-sheet">
            <div className="flex flex-wrap gap-2" data-testid="paylink-reasons">
                {REASONS.map((r) => (
                    <button key={r} onClick={() => pickReason(r)} data-testid={`paylink-reason-${r.toLowerCase().replace(" ", "-")}`}
                        className={`min-h-[44px] flex-1 border px-2 text-[15px] font-medium ${reason === r ? "border-[#1a1a1a] bg-[#1a1a1a] text-white" : "border-[#1a1a1a] bg-white"}`}>{r}</button>
                ))}
            </div>
            {reason === "Rush" && (
                <div className="mt-2 flex gap-2" data-testid="paylink-rush">
                    {Object.entries(rushPct || { "2 weeks": 50, "1 week": 75, "1 day": 150 }).map(([k, v]) => (
                        <button key={k} onClick={() => pickRush(k)} data-testid={`paylink-rush-${k.replace(/\s/g, "")}`}
                            className={`min-h-[44px] flex-1 border px-2 text-[14px] ${rush === k ? "border-[#1a1a1a] bg-[#1a1a1a] text-white" : "border-[#1a1a1a] bg-white"}`}>{k} +{v}%</button>
                    ))}
                </div>
            )}
            <div className="mt-3">
                <Field label="Amount ($)"><input type="number" inputMode="decimal" min="1" step="0.01" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="paylink-amount" /></Field>
            </div>
            {link ? (
                <div className="mt-3 border border-[#1a1a1a] p-2" data-testid="paylink-result">
                    <p className="break-all text-[14px]">{link}</p>
                    <button onClick={async () => setCopied(await copyText(link))} data-testid="paylink-copy" className="mt-2 inline-flex min-h-[44px] items-center gap-1 underline">
                        {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
                    </button>
                </div>
            ) : (
                <div className="mt-3"><Primary onClick={build} disabled={busy} testid="paylink-build" full>Copy</Primary></div>
            )}
        </Sheet>
    );
}

/** 8.3 Reprint: optional reason → a linked $0 copy in the same queue (13thav's for a consignment line). */
export function ReprintSheet({ order, onDone, onClose, base = "/vendor/orders" }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const consign = order.prints !== "self" && base === "/vendor/orders";
    const go = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`${base}/${order.id}/reprint`, { reason });
            toast.success(`${data.order_number} added to ${data.to_13thav ? "13thav's" : "your"} queue`);
            onDone(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    return (
        <Sheet title={consign ? "Ask 13thav to reprint" : "Reprint"} onClose={onClose} testid="reprint-sheet">
            <Field label="Reason (optional)"><input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reprint-reason" placeholder="e.g. print came out light" /></Field>
            <div className="mt-3"><Primary onClick={go} disabled={busy} testid="reprint-go" full>{consign ? "Ask 13thav to reprint" : "Reprint"}</Primary></div>
        </Sheet>
    );
}

/** 8.3 Reorder for customer: every line again at today's prices, then one pay link to copy. */
export function ReorderSheet({ order, onClose, base = "/vendor/orders" }) {
    const [res, setRes] = useState(null);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const go = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`${base}/${order.id}/reorder`, { origin_url: window.location.origin });
            setRes(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    return (
        <Sheet title="Reorder for customer" onClose={onClose} testid="reorder-sheet">
            {res ? (
                <div data-testid="reorder-result">
                    <p className="text-[16px]">New order <b>{res.order_number}</b> · {money(res.total)}{Math.abs(Number(res.total) - Number(res.old_total)) >= 0.01 ? ` (was ${money(res.old_total)})` : ""}</p>
                    <p className="mt-1 text-[14px] text-[#4d4d4d]">The customer opens this link and taps Pay now — 2 taps on their phone.</p>
                    <p className="mt-2 break-all border border-[#1a1a1a] p-2 text-[14px]" data-testid="reorder-link">{res.pay_url}</p>
                    <Primary onClick={async () => setCopied(await copyText(res.pay_url))} testid="reorder-copy" full className="mt-3">
                        {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Send pay link</>}
                    </Primary>
                </div>
            ) : (
                <>
                    <p className="text-[15px] text-[#4d4d4d]">Copies every line, file and note into a new order at today's prices.</p>
                    <div className="mt-3"><Primary onClick={go} disabled={busy} testid="reorder-go" full>Reorder {money(order.order_total)}</Primary></div>
                </>
            )}
        </Sheet>
    );
}
